import { Link, Navigate } from '@tanstack/react-router'
import type { DealerReviewAction, DealerRole, DealerTexView, DealerView, MeResponse } from '@tex/finance-shared'
import { type FormEvent, useState } from 'react'
import { Alert, Button, Card, Field, Input, Row, Select, StatusBadge, Textarea } from '../components/ui'
import { errorMessage } from '../lib/api'
import { formatDate } from '../lib/format'
import {
  useCancelInvitation,
  useDealer,
  useDealerTeam,
  useInviteMember,
  useReviewDealer,
  useUpdateDealerBranding,
  useUpdateDealerProfile,
} from '../lib/dealers'
import { isTexStaff, useMe } from '../lib/session'

/** Mirrors `canDealer` in apps/api/src/auth/policy.ts so we only show controls the API will accept. */
function permissions(me: MeResponse, dealer: DealerView) {
  const tex = isTexStaff(me)
  const opsAdmin = me.tex_role === 'ops_admin'
  const membership = me.dealers.find((d) => d.dealer_id === dealer.id)
  const admin = membership !== undefined && membership.role !== 'member'
  const manage = opsAdmin || (!tex && admin && dealer.status !== 'suspended')
  return { tex, manage, inviteRoles: (tex ? ['owner', 'admin', 'member'] : ['admin', 'member']) as DealerRole[] }
}

export function DealerDetailPage({ dealerId }: { dealerId: string }) {
  const me = useMe()
  const dealer = useDealer(dealerId)

  if (me.data === null) return <Navigate to="/sign-in" />
  if (dealer.isError) return <Alert tone="error">{errorMessage(dealer.error)}</Alert>
  if (!me.data || !dealer.data) return <p className="text-sm text-neutral-500">Loading…</p>

  const d = dealer.data
  const perms = permissions(me.data, d)

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          {perms.tex && (
            <Link to="/dealers" className="text-xs text-neutral-500 hover:underline">
              ← All dealers
            </Link>
          )}
          <h1 className="font-display text-3xl font-bold text-neutral-900 flex items-center gap-3">
            {d.brand.display_name}
            <StatusBadge status={d.status} />
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            {d.legal_name} · public page{' '}
            {d.status === 'active' ? (
              <code className="text-xs">/api/v1/dealers/{d.slug}/public</code>
            ) : (
              <span className="text-neutral-500">unpublished until active</span>
            )}
          </p>
        </div>
      </div>

      {d.status === 'suspended' && (
        <Alert tone="error">
          Suspended{d.suspended_reason ? `: ${d.suspended_reason}` : ''}. Profile, branding and team are read-only; existing cases
          are paused.
        </Alert>
      )}
      {d.status === 'pending_review' && !perms.tex && (
        <Alert tone="info">Tex is reviewing this dealership. You can prepare your profile and team in the meantime.</Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <ProfileCard dealer={d} canEdit={perms.manage} />
        <BrandingCard dealer={d} canEdit={perms.manage} />
        {d.audience === 'tex' && <ReviewCard dealer={d} />}
        <TeamCard dealerId={d.id} canManage={perms.manage} inviteRoles={perms.inviteRoles} />
      </div>
    </section>
  )
}

function ProfileCard({ dealer, canEdit }: { dealer: DealerView; canEdit: boolean }) {
  const update = useUpdateDealerProfile(dealer.id)
  const [editing, setEditing] = useState(false)
  const [legalName, setLegalName] = useState(dealer.legal_name)
  const [tradingName, setTradingName] = useState(dealer.trading_name ?? '')
  const [website, setWebsite] = useState(dealer.website ?? '')
  const [categories, setCategories] = useState(dealer.equipment_categories.join(', '))

  function submit(e: FormEvent) {
    e.preventDefault()
    update.mutate(
      {
        legal_name: legalName.trim(),
        // Blank input clears the field; `undefined` would be dropped by JSON and leave it unchanged.
        trading_name: tradingName.trim() || null,
        website: website.trim() || null,
        equipment_categories: categories
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      },
      { onSuccess: () => setEditing(false) },
    )
  }

  return (
    <Card
      title="Profile"
      actions={
        canEdit && !editing ? (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
        ) : undefined
      }
    >
      {editing ? (
        <form onSubmit={submit} className="space-y-3">
          <Field label="Legal name">
            <Input required minLength={2} value={legalName} onChange={(e) => setLegalName(e.target.value)} />
          </Field>
          <Field label="Trading name">
            <Input value={tradingName} onChange={(e) => setTradingName(e.target.value)} />
          </Field>
          <Field label="Website">
            <Input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </Field>
          <Field label="Equipment categories" hint="Comma-separated, e.g. excavators, skid steers">
            <Input value={categories} onChange={(e) => setCategories(e.target.value)} />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" disabled={update.isPending}>
              Save
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
          {update.isError && <Alert tone="error">{errorMessage(update.error)}</Alert>}
        </form>
      ) : (
        <dl className="divide-y divide-neutral-100">
          <Row label="Legal name" value={dealer.legal_name} />
          <Row label="Trading name" value={dealer.trading_name} />
          <Row label="Website" value={dealer.website} />
          <Row label="Equipment" value={dealer.equipment_categories.join(', ')} />
          <Row
            label="Address"
            value={dealer.address ? `${dealer.address.city}, ${dealer.address.state} ${dealer.address.postal_code}` : null}
          />
          <Row label="Contacts" value={dealer.contacts.map((c) => `${c.name} <${c.email}>`).join('; ')} />
          <Row label="Slug" value={dealer.slug} />
          <Row label="Updated" value={formatDate(dealer.updated_at)} />
        </dl>
      )}
    </Card>
  )
}

function BrandingCard({ dealer, canEdit }: { dealer: DealerView; canEdit: boolean }) {
  const update = useUpdateDealerBranding(dealer.id)
  const [displayName, setDisplayName] = useState(dealer.brand.display_name)
  const [accent, setAccent] = useState(dealer.brand.accent_color ?? '')

  function submit(e: FormEvent) {
    e.preventDefault()
    update.mutate({ display_name: displayName.trim(), accent_color: accent.trim() || null })
  }

  return (
    <Card title="Branding">
      <p className="mb-3 text-xs text-neutral-500">
        What borrowers see on their private application page. Logos land with document uploads in M2.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Display name">
          <Input required disabled={!canEdit} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field label="Accent colour" hint="Hex, e.g. #1f6f43">
          <div className="flex items-center gap-2">
            <Input
              disabled={!canEdit}
              pattern="^#[0-9a-fA-F]{6}$"
              placeholder="#000000"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
            />
            <span
              aria-hidden
              className="h-8 w-8 shrink-0 rounded border border-[var(--border-subtle)]"
              style={{ backgroundColor: /^#[0-9a-fA-F]{6}$/.test(accent) ? accent : '#f5f5f4' }}
            />
          </div>
        </Field>
        {canEdit && (
          <Button type="submit" disabled={update.isPending}>
            Save branding
          </Button>
        )}
        {update.isError && <Alert tone="error">{errorMessage(update.error)}</Alert>}
        {update.isSuccess && <Alert tone="success">Saved.</Alert>}
      </form>
    </Card>
  )
}

function ReviewCard({ dealer }: { dealer: DealerTexView }) {
  const review = useReviewDealer(dealer.id)
  const [method, setMethod] = useState(dealer.verification_method ?? '')
  const [notes, setNotes] = useState('')
  const [reason, setReason] = useState('')

  function act(action: DealerReviewAction) {
    review.mutate(
      {
        action,
        verification_method: action === 'activate' ? method.trim() || undefined : undefined,
        reviewer_notes: notes.trim() || undefined,
        reason: action === 'suspend' ? reason.trim() || undefined : undefined,
      },
      { onSuccess: () => setNotes('') },
    )
  }

  return (
    <Card title="Tex review">
      <dl className="mb-4 divide-y divide-neutral-100">
        <Row label="Verification" value={dealer.verification_method} />
        <Row label="Verified" value={dealer.verified_at ? `${formatDate(dealer.verified_at)} by ${dealer.verified_by}` : null} />
        <Row label="Suspended" value={dealer.suspended_at ? `${formatDate(dealer.suspended_at)}: ${dealer.suspended_reason}` : null} />
        <Row label="Members / cases" value={`${dealer.member_count} / ${dealer.case_count}`} />
      </dl>
      {dealer.reviewer_notes && (
        <p className="mb-4 whitespace-pre-wrap rounded bg-neutral-50 p-2 text-xs text-neutral-700">{dealer.reviewer_notes}</p>
      )}
      <div className="space-y-3">
        <Field label="Reviewer notes" hint="Appended to the internal trail with this action.">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {dealer.status !== 'active' && (
          <div className="space-y-2">
            <Field label="Verification method" hint="Required to activate, e.g. 'Secretary of State lookup + phone call with owner'.">
              <Input value={method} onChange={(e) => setMethod(e.target.value)} />
            </Field>
            <Button disabled={review.isPending} onClick={() => act(dealer.status === 'suspended' ? 'reinstate' : 'activate')}>
              {dealer.status === 'suspended' ? 'Reinstate' : 'Activate'}
            </Button>
          </div>
        )}
        {dealer.status !== 'suspended' && (
          <div className="space-y-2">
            <Field label="Suspension reason" hint="Required to suspend; shown to the dealer.">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <Button variant="danger" disabled={review.isPending} onClick={() => act('suspend')}>
              Suspend
            </Button>
          </div>
        )}
        {review.isError && <Alert tone="error">{errorMessage(review.error)}</Alert>}
      </div>
    </Card>
  )
}

function TeamCard({ dealerId, canManage, inviteRoles }: { dealerId: string; canManage: boolean; inviteRoles: DealerRole[] }) {
  const team = useDealerTeam(dealerId)
  const invite = useInviteMember(dealerId)
  const cancel = useCancelInvitation(dealerId)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<DealerRole>(inviteRoles[inviteRoles.length - 1] ?? 'member')

  function submit(e: FormEvent) {
    e.preventDefault()
    invite.mutate({ email: email.trim(), role }, { onSuccess: () => setEmail('') })
  }

  return (
    <Card title="Team">
      {team.isError && <Alert tone="error">{errorMessage(team.error)}</Alert>}
      <ul className="divide-y divide-neutral-100 text-sm">
        {team.data?.members.map((m) => (
          <li key={m.id} className="flex justify-between py-1.5">
            <span>
              {m.name ?? m.email} <span className="text-neutral-500">{m.name ? `· ${m.email}` : ''}</span>
            </span>
            <span className="text-xs uppercase text-neutral-500">{m.role}</span>
          </li>
        ))}
        {team.data && team.data.members.length === 0 && <li className="py-1.5 text-neutral-500">No members yet.</li>}
      </ul>

      {team.data?.invitations && team.data.invitations.length > 0 && (
        <>
          <h3 className="mt-4 mb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500">Pending invitations</h3>
          <ul className="divide-y divide-neutral-100 text-sm">
            {team.data.invitations.map((i) => (
              <li key={i.id} className="flex items-center justify-between py-1.5">
                <span>
                  {i.email} <span className="text-xs uppercase text-neutral-500">· {i.role}</span>
                  <span className="block text-xs text-neutral-500">expires {formatDate(i.expires_at)}</span>
                </span>
                {canManage && (
                  <Button variant="danger" disabled={cancel.isPending} onClick={() => cancel.mutate(i.id)}>
                    Cancel
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {canManage && (
        <form onSubmit={submit} className="mt-4 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-48">
            <Field label="Invite by email">
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
          </div>
          <Field label="Role">
            <Select value={role} onChange={(e) => setRole(e.target.value as DealerRole)}>
              {inviteRoles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit" disabled={invite.isPending}>
            Invite
          </Button>
          {invite.isError && <Alert tone="error">{errorMessage(invite.error)}</Alert>}
          {cancel.isError && <Alert tone="error">{errorMessage(cancel.error)}</Alert>}
        </form>
      )}
    </Card>
  )
}
